import React, { Component } from 'react';
import PropTypes from 'prop-types';
import config from 'cityads-v3/config';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';
import { Helmet } from 'react-helmet';
import Marquee from "react-fast-marquee";

import { Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';

import UserdocsLink, { USERDOCS_HOST } from 'cityads-v3/components/Links/UserdocsLink';

import { getTopWMsData } from 'cityads-v3/actions/home';

import Balaclava from 'cityads-v3/components/Balaclava';
import Card from 'cityads-v3/components/Card/Card';
import Carousel from 'cityads-v3/components/Carousel/Carousel';
import ClickableCardWithPopup from 'cityads-v3/components/CardWithPopup/ClickableCardWithPopup';
import HomeWidgetLoading from 'cityads-v3/components/Home/Widgets/Loading';
import HomeWidgetTable from 'cityads-v3/components/Home/Widgets/Table';
import ScrollToTopOnMount from 'cityads-v3/containers/misc/ScrollToTopOnMount';
import StartWorkButton, { ALLOWED_PATHS as STARTWORK_PATHS } from 'cityads-v3/components/Buttons/StartWorkButton';
import ToolsCarousel, { ALLOWED_PATHS as TOOLS_PATHS } from 'cityads-v3/components/Carousel/ToolsCarousel';
import Typography from '@material-ui/core/Typography'
import { PlaceIcon1, PlaceIcon2, PlaceIcon3 } from './icons';

import { yandexReachGoal } from 'cityads-v3/utils/counterUtils';
import { sassExportsToObjectRecursive } from 'cityads-v3/utils/styleUtils';
import classNames from './Publisher.scss';

import { PUBLISHER_PARTNERS, FEATURED_PAYMENT_METHODS, ADVERTISER_LOGOS_BY_LANGUAGE } from './Landing.data.js';

export const ALLOWED_PATHS = [
  ...STARTWORK_PATHS,
  ...TOOLS_PATHS,
  'payments',
  'more',
];

const sassExports = sassExportsToObjectRecursive(classNames);

const partnerCardStyles = {
  ...sassExports.partnerCardStyles,
  cardHeaderClasses: {
    root: classNames.carouselHeader,
    title: classNames.carouselHeaderTitle,
    subheader: classNames.carouselSubheader,
  },
  collapseClasses: {
    root: classNames.carouselCollapse,
    entered: classNames.carouselCollapseExpanded,
  },
};

@connect(
  state => ({
    user                  : state.auth.user,
    language              : state.language.language,
    toolsCarouselItems    : state.home.toolsCarouselItems,
    topWMs                : state.home.topWMs,
    isLoadingTopWMsData   : state.home.isLoadingTopWMsData,
    isTopWMsDataLoaded    : state.home.isTopWMsDataLoaded,
  })
)
@withTranslation(['main'], { i18n, wait: !!window })
export default class Publisher extends Component {
  static propTypes = {
    user               : PropTypes.object,
    language           : PropTypes.string,
    toolsCarouselItems : PropTypes.array,
    topWMs             : PropTypes.shape({ values: PropTypes.array, currency: PropTypes.string }),
    isLoadingTopWMsData: PropTypes.bool,
    isTopWMsDataLoaded : PropTypes.bool,
  }

  componentDidMount() {
    if (!this.props.isTopWMsDataLoaded && !this.props.isLoadingTopWMsData) {
      this.props.dispatch(getTopWMsData());
    }
  }

  clickStartWork() {
    yandexReachGoal('CLICK_STARTWORK_BUTTON_PUBLISHER');
  }

  renderPublisherPosition(position) {
    const Component = [PlaceIcon1, PlaceIcon2, PlaceIcon3][position - 1];

    if (Component) {
      return <Component className={classNames.placeIcon} />;
    }

    return position;
  }

  render () {
    const { user, language, match, topWMs, isTopWMsDataLoaded, toolsCarouselItems, t } = this.props;
    const publisherPartnersFiltered = PUBLISHER_PARTNERS.filter(item => filterPublisherPartners(item, language));

    let subpagePrefix = match.path.split(':')[0];
    subpagePrefix = subpagePrefix.substr(0, subpagePrefix.length - 1);

    return (
      <div className={classNames.publisherPage}>
        <ScrollToTopOnMount/>

        <Helmet
          title = {t('publisher_page.title')}
          meta  = {[
            { name: 'description', content: t('publisher_page.description') },
            { name: 'keywords', content: t('publisher_page.keywords') },
          ]}
        />
        <Balaclava
          menuValue = {'publisher'}
        />

        <div className={classNames.cta}>
          <div className={classNames.ctaWrapper}>
            <div className={classNames.ctaText}>
              <div className={classNames.ctaPreHeader}>
                {t('ca_media')}
              </div>
              <h1 className={classNames.ctaHeader}>
                {t('publisher_page.headerSlogan')}
              </h1>

              <div className={classNames.ctaContent}>
                {t('publisher_page.headerText')}
              </div>

              <div className={classNames.ctaButton}>
                <StartWorkButton
                  label        = {t('publisher_page.startWorkButton')}
                  user         = {user}
                  currentPath  = {subpagePrefix}
                  userRoleCode = "W"
                  registerAs   = "publisher"
                  onClick      = {this.clickStartWork}
                />
              </div>
            </div>

            <div className={classNames.ctaCarouselContainer}>
              <Carousel
                className        = {classNames.ctaCarousel}
                items            = {publisherPartnersFiltered}
                limits           = {{ 0: 1 }}
                autoplay         = {true}
                isCardExpandable = {true}
                isStretched      = {false}
                cardProps        = {partnerCardStyles}
              />
            </div>
          </div>
        </div>


        <div className={classNames.advertisers}>
          <Marquee gradient={false}>
            {(ADVERTISER_LOGOS_BY_LANGUAGE[language] ?? ADVERTISER_LOGOS_BY_LANGUAGE[config.DEFAULT_LANGUAGE]).map((advertiser, idx) =>
              <img key={idx} alt={advertiser.title} src={advertiser.image} className={classNames.advertiser} />
            )}
          </Marquee>
        </div>


        <div className={classNames.advantages}>
          <Typography component="h2" color="textSecondary" className={classNames.advantagesHeader}>
            {t('publisher_page.advantages')}
            <Typography component="div" className={classNames.advantagesHeaderName}>
              {t('publisher_page.cityadsMedia')}
            </Typography>
          </Typography>

          <div className={classNames.advantagesList}>
            <div className={classNames.advantage}>
              <div className={classNames.advantageIconMoney}>
              </div>
              <Typography component="h3" className={classNames.advantageHeader} dangerouslySetInnerHTML={{ __html: t('publisher_page.advantage1Header') }} />
              <Typography color="textSecondary" className={classNames.advantageText}>
                {t('publisher_page.advantage1Text')}
              </Typography>
            </div>

            <div className={classNames.advantage}>
              <div className={classNames.advantageIconLifebelt}>
              </div>
              <Typography component="h3" className={classNames.advantageHeader} dangerouslySetInnerHTML={{ __html: t('publisher_page.advantage2Header') }} />
              <Typography color="textSecondary" className={classNames.advantageText}>
                {t('publisher_page.advantage2Text')}
              </Typography>
            </div>

            <div className={classNames.advantage}>
              <div className={classNames.advantageIconMac}>
              </div>
              <Typography component="h3" className={classNames.advantageHeader} dangerouslySetInnerHTML={{ __html: t('publisher_page.advantage3Header') }} />
              <Typography color="textSecondary" className={classNames.advantageText}>
                {t('publisher_page.advantage3Text')}
              </Typography>
            </div>

            <div className={classNames.advantage}>
              <div className={classNames.advantageIconHeart}>
              </div>
              <Typography component="h3" className={classNames.advantageHeader} dangerouslySetInnerHTML={{ __html: t('publisher_page.advantage4Header') }} />
              <Typography color="textSecondary" className={classNames.advantageText}>
                {t('publisher_page.advantage4Text')}
              </Typography>
            </div>
          </div>
        </div>


        <div className={classNames.features}>
          <div className={classNames.tools}>
            <ToolsCarousel
              className = {classNames.toolsCarousel}
              limits    = {{ 0: 1, 544: 2, 800: 4 }}
              items     = {toolsCarouselItems
                .filter(item => item.orderWeb > 0)
                .sort((item1, item2) => item1.orderWeb - item2.orderWeb)
                .map(item => ({
                  ...item,
                  title          : t('carouselItems.' + item.translateKey + '.title'),
                  children       : t('carouselItems.' + item.translateKey + '.subtitle'),
                  moreButtonTitle: t('learn_more'),
                }))}
              moreRoot  = {subpagePrefix}
              lessLink  = {subpagePrefix}
            />
          </div>

          <div className={classNames.featureList}>
            <div className={classNames.featureRow}>
              <div className={classNames.featureCol}>
                <ClickableCardWithPopup
                  className    = {classNames.feature}
                  title        = {<h2 className={classNames.cardHeader}>{t('card1.title')}</h2>}
                  subtitle     = {t('card1.subtitle')}
                  image        = {require('./img/publisher/money-magnet.jpg')}
                  moreLink     = {subpagePrefix + '/more'}
                  lessLink     = {subpagePrefix}
                  articleClass = "PublisherMore"
                >
                  {t('card1.text')}
                </ClickableCardWithPopup>

                <Card
                  className  = {classNames.feature}
                  title      = {<h2 className={classNames.cardHeader}>{t('card6.title')}</h2>}
                  subtitle   = {t('card6.subtitle')}
                  actions    = {[
                    {
                      title: USERDOCS_HOST,
                      href: '/',
                      isPrimary: true,
                      component: UserdocsLink
                    }
                  ]}
                  {...sassExports.knowledgeCard}
                  cardHeaderClasses = {{
                    root: classNames.knowledgeHeader,
                    title: classNames.knowledgeTitle,
                    subheader: classNames.knowledgeSubheader,
                  }}
                >
                  {t('card6.text')}
                </Card>
              </div>

              <div className={classNames.featureCol}>
                <ClickableCardWithPopup
                  className       = {classNames.feature}
                  title           = {<h2 className={classNames.cardHeader}>{t('card7.title')}</h2>}
                  subtitle        = {t('card7.subtitle')}
                  moreLink        = {subpagePrefix + '/payments'}
                  moreButtonTitle = {t('more_info')}
                  lessLink        = {subpagePrefix}
                  articleClass    = "Payments"
                  {...sassExports.paymentMethodsCard}
                >
                  <div className={classNames.paymentMethods}>
                    {FEATURED_PAYMENT_METHODS.map((method, index) => (
                      <img key={index} className={classNames.paymentMethod} src={method.image} alt={method.title} title={method.title} />
                    ))}
                  </div>
                </ClickableCardWithPopup>

                <Card
                  className = {classNames.feature}
                  title     = {<h2 className={classNames.cardHeader}>{t('card8.title')}</h2>}
                >
                  <React.Fragment>
                    {isTopWMsDataLoaded ?
                      <HomeWidgetTable
                        columns = {[
                          { name: 'position', title: '#', type: 'string' },
                          { name: 'title', title: t('publisher'), type: 'string' },
                          { name: 'value', title: t('income'), type: 'number', precision: 0 },
                          { name: 'currency', title: t('currency'), type: 'string' },
                        ]}
                        rows = {topWMs ? topWMs.values.map((value, index) => ({
                          position: this.renderPublisherPosition(index + 1),
                          title : t('publisher'),
                          value,
                          currency: topWMs.currency,
                        })) : []}
                      /> :
                      <HomeWidgetLoading />
                    }
                    {user ?
                      null:
                      <div className={classNames.joinContainer}>
                        <Button
                          variant   = "contained"
                          color     = "primary"
                          fullWidth
                          component = {Link}
                          to        = {`${subpagePrefix}/register/publisher`}
                        >
                          {t('card8.cta')}
                        </Button>
                      </div>
                    }
                  </React.Fragment>
                </Card>
              </div>
            </div>
          </div>
        </div>


        <div className={classNames.successes}>
          <h2 className={classNames.successesHeader}>
            {t('publisher_page.successes')}
          </h2>

          <div className={classNames.partners}>
            <Carousel
              className        = {classNames.partnersCarousel}
              items            = {publisherPartnersFiltered}
              limits           = {{ 0: 1, 500: 2, 700: 3 }}
              isCardExpandable = {true}
              isStretched      = {false}
              cardProps        = {partnerCardStyles}
            />
          </div>
        </div>
      </div>
    );
  }
}

export function filterPublisherPartners(item, language) {
  if (language === 'ru') return item.language !== 'en';

  return item.language !== 'ru';
}
